import { useNavigate, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import {
  ChevronLeft, ChevronRight, MapPin, Globe2, Bell, UserIcon, Shield, HelpCircle, LogOut,
} from "lucide-react";
import { useState } from "react";

const LANGS = [
  { code: "en", label: "English",  native: "English" },
  { code: "he", label: "Hebrew",   native: "עברית" },
  { code: "ar", label: "Arabic",   native: "العربية" },
];

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { language, setLanguage, isRTL } = useI18n();
  const navigate = useNavigate();
  const [showLangs, setShowLangs] = useState(false);

  const cityName =
    language === "ar" ? (user?.city_ar || user?.city_en || user?.city_he)
    : language === "he" ? (user?.city_he || user?.city_en || user?.city_ar)
    : (user?.city_en || user?.city_he || user?.city_ar);

  const currentLangLabel = LANGS.find((l) => l.code === language)?.native || "—";

  const topBar = (
    <div className="h-14 px-4 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="settings-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[22px] text-[var(--te-text)] flex-1">
        {t("settings_title", language)}
      </h1>
    </div>
  );

  const Chev = isRTL ? ChevronLeft : ChevronRight;

  return (
    <MobileShell topBar={topBar}>
      <div className="px-4 pt-4 pb-6 space-y-6">

        {/* Locality */}
        <Section title={t("settings_locality", language)}>
          <Row
            icon={<MapPin size={18} />}
            label={
              <span className="flex items-center gap-1.5">
                <span aria-hidden>📍</span>
                <span className="font-medium">{cityName || "—"}</span>
              </span>
            }
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            to="/profile/edit"
            testId="settings-locality-row"
          />
        </Section>

        {/* Preferences */}
        <Section title={t("settings_preferences", language)}>
          <Row
            icon={<Globe2 size={18} />}
            label={
              <span className="font-medium">
                {t("settings_language", language)} · {currentLangLabel}
              </span>
            }
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            onClick={() => setShowLangs((v) => !v)}
            testId="settings-language-row"
          />
          {showLangs && (
            <div className="bg-[var(--te-bg-soft)] border-t border-[var(--te-divider)]">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLanguage(l.code); setShowLangs(false); }}
                  data-testid={`settings-lang-${l.code}`}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-white"
                >
                  <span className="text-sm text-[var(--te-text)]" dir="auto">{l.native}</span>
                  {language === l.code && <span className="w-2.5 h-2.5 rounded-full bg-[var(--te-primary)]" />}
                </button>
              ))}
            </div>
          )}
          <Divider />
          <Row
            icon={<Bell size={18} />}
            label={<span className="font-medium">{t("settings_notifications", language)}</span>}
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            to="/notifications"
            testId="settings-notifications-row"
          />
        </Section>

        {/* Account */}
        <Section title={t("settings_account", language)}>
          <Row
            icon={<UserIcon size={18} />}
            label={<span className="font-medium">{t("settings_edit_profile", language)}</span>}
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            to="/profile/edit"
            testId="settings-edit-profile-row"
          />
          <Divider />
          <Row
            icon={<Shield size={18} />}
            label={<span className="font-medium">{t("settings_privacy", language)}</span>}
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            to="/privacy"
            testId="settings-privacy-row"
          />
          <Divider />
          <Row
            icon={<HelpCircle size={18} />}
            label={<span className="font-medium">{t("settings_help", language)}</span>}
            chev={<Chev size={16} className="text-[var(--te-text-muted)]" />}
            to="/terms"
            testId="settings-help-row"
          />
        </Section>

        {/* Logout */}
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="settings-logout-btn"
          className="w-full te-card te-card-hover p-4 flex items-center justify-center gap-3 text-[var(--te-danger)] font-semibold te-fade-up"
        >
          <LogOut size={18} />
          <span>{t("settings_logout", language)}</span>
        </button>
      </div>
    </MobileShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="te-fade-up">
      {title && (
        <h2 className="te-eyebrow mb-2 px-1" data-testid={`settings-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>{title}</h2>
      )}
      <div className="te-card overflow-hidden">
        {children}
      </div>
    </section>
  );
}

function Row({ icon, label, chev, to, onClick, testId }) {
  const inner = (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <div className="te-icon-tile">{icon}</div>
      <div className="flex-1 text-[15px] text-[var(--te-text)]">{label}</div>
      {chev}
    </div>
  );
  if (to) {
    return <Link to={to} data-testid={testId} className="block hover:bg-[var(--te-bg-soft)]">{inner}</Link>;
  }
  return (
    <button onClick={onClick} data-testid={testId} className="w-full text-left hover:bg-[var(--te-bg-soft)]">
      {inner}
    </button>
  );
}

function Divider() {
  return <div className="border-t border-[var(--te-divider)] mx-4" />;
}
